(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ShSocialCommunity extends HTMLElement {
    connectedCallback() {
      this.countUpAll();
      this.initReels();
      this.initModal();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.closeModal();
    }

    /* ---------- count up ---------- */

    countUpAll() {
      const targets = this.querySelectorAll('[data-count]');
      if (!targets.length || REDUCED.matches || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.observer.unobserve(entry.target);
            this.countUp(entry.target);
          });
        },
        { threshold: 0.4 }
      );

      targets.forEach((el) => this.observer.observe(el));
    }

    countUp(el) {
      const raw = el.textContent.trim();
      const match = raw.match(/^(\D*)([\d.,]+)(.*)$/);
      if (!match) return;

      const [, before, numText, after] = match;
      const decimalSep = /,\d{1,2}$/.test(numText) ? ',' : /\.\d{1,2}$/.test(numText) ? '.' : '';
      const normalised = decimalSep ? numText.replace(decimalSep, '.') : numText.replace(/[.,]/g, '');
      const target = parseFloat(normalised);
      if (!isFinite(target)) return;

      const decimals = decimalSep ? 1 : 0;
      const duration = 3600;
      let start;

      const format = (value) => {
        const fixed = value.toFixed(decimals);
        return before + (decimalSep ? fixed.replace('.', decimalSep) : fixed) + after;
      };

      el.textContent = format(0);

      const step = (now) => {
        if (start === undefined) start = now;
        const progress = Math.min((now - start) / duration, 1);
        // Quadratic ease-out: gentler tail than cubic, so the last digits
        // stay readable instead of snapping to the final value.
        const eased = 1 - Math.pow(1 - progress, 2);
        el.textContent = format(target * eased);
        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = raw;
      };

      requestAnimationFrame(step);
    }

    /* ---------- reels rail ---------- */

    initReels() {
      this.rail = this.querySelector('[data-reels]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (!this.rail || !this.dots.length) return;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const card = this.rail.children[index];
          if (card) this.rail.scrollTo({ left: card.offsetLeft - this.rail.offsetLeft, behavior: 'smooth' });
        });
      });

      this.rail.addEventListener('scroll', () => this.syncDots(), { passive: true });
      this.syncDots();
    }

    syncDots() {
      // Mapped to scroll progress, not card alignment — the rail runs out of
      // scroll before the last card reaches the left edge, so it could never win.
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const steps = this.dots.length - 1;
      const active = max > 1 && steps > 0 ? Math.round((this.rail.scrollLeft / max) * steps) : 0;
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === active ? 'true' : 'false'));
    }

    /* ---------- modal carousel ---------- */

    initModal() {
      this.modal = this.querySelector('[data-modal]');
      this.stage = this.querySelector('[data-stage]');
      if (!this.modal || !this.stage) return;

      this.triggers = Array.from(this.querySelectorAll('[data-open]'));
      this.segments = Array.from(this.modal.querySelectorAll('[data-segment]'));

      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.openModal(index);
        });
      });

      this.modal.querySelector('[data-close]')?.addEventListener('click', () => this.closeModal());
      this.modal.querySelector('[data-mprev]')?.addEventListener('click', () => this.step(-1));
      this.modal.querySelector('[data-mnext]')?.addEventListener('click', () => this.step(1));

      this.segments.forEach((segment, index) => {
        segment.addEventListener('click', () => this.show(index));
      });

      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });

      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.closeModal();
        else if (event.key === 'ArrowLeft') this.step(-1);
        else if (event.key === 'ArrowRight') this.step(1);
      };

      this.initSwipe();
    }

    /* Mobile has no arrows by design, so swipe is the only way through the
       reels. Listeners sit on the modal, not the stage, because the stage is
       replaced on every slide change. */
    initSwipe() {
      // A slow drag needs distance; a quick flick does not. Without the
      // velocity path, short fast swipes feel like the UI ignored them.
      const DISTANCE = 45;
      const FLICK_DISTANCE = 18;
      const FLICK_SPEED = 0.4; // px per ms
      let x = null;
      let y = null;
      let t = 0;

      this.modal.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) return;
          x = event.touches[0].clientX;
          y = event.touches[0].clientY;
          t = event.timeStamp;
        },
        { passive: true }
      );

      this.modal.addEventListener(
        'touchend',
        (event) => {
          if (x === null) return;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - x;
          const dy = touch.clientY - y;
          const elapsed = Math.max(event.timeStamp - t, 1);
          x = null;
          y = null;

          // Either axis moves between reels — a vertical swipe is the native
          // gesture on a full-screen vertical video.
          const horizontal = Math.abs(dx) > Math.abs(dy);
          const travel = horizontal ? dx : dy;
          const speed = Math.abs(travel) / elapsed;

          if (Math.abs(travel) < DISTANCE && !(Math.abs(travel) > FLICK_DISTANCE && speed > FLICK_SPEED)) return;
          this.step(travel < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    openModal(index) {
      this.lastTrigger = this.triggers[index];
      this.modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      this.show(index);
      this.modal.querySelector('[data-close]')?.focus();
    }

    step(direction) {
      if (this.modal.hasAttribute('hidden') || !this.triggers.length) return;
      const next = (this.current + direction + this.triggers.length) % this.triggers.length;
      this.show(next, direction);
    }

    show(index, direction) {
      const trigger = this.triggers[index];
      const template = trigger?.querySelector('template[data-video]');
      const from = this.current;
      this.current = index;

      // Restarting the animation needs the class gone for one frame.
      if (from !== undefined && from !== index) {
        const dir = direction || (index > from ? 1 : -1);
        this.stage.removeAttribute('data-enter');
        void this.stage.offsetWidth;
        this.stage.setAttribute('data-enter', dir > 0 ? 'next' : 'prev');
      }

      this.segments.forEach((segment, i) => segment.setAttribute('aria-current', i === index ? 'true' : 'false'));

      if (!template) {
        this.stage.replaceChildren();
        return;
      }

      this.stage.replaceChildren(template.content.cloneNode(true));
      this.startVideo(this.stage.querySelector('video'), template.dataset.poster);
    }

    /* iOS needs more care than play(): inline playback must be declared on the
       element, a poster keeps the frame from going black before data arrives,
       and play() must be called in the same task as the tap or Safari blocks
       it. If it is still refused, retry muted rather than showing nothing. */
    startVideo(video, poster) {
      if (!video) return;

      video.setAttribute('controls', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      if (poster) video.setAttribute('poster', poster);

      // Muted is the only state iOS always allows to start — unmuted playback is
      // refused outright in Low Power Mode, and it can report itself as playing
      // while rendering nothing. Start muted, then lift it once frames run.
      video.muted = true;
      video.volume = 1;

      const play = () => video.play?.() ?? Promise.reject();

      const unmute = () => {
        video.muted = false;
        // If Safari refuses, stay muted and playing rather than stalling.
        if (video.paused) play().catch(() => { video.muted = true; });
      };

      video.addEventListener('playing', unmute, { once: true });

      play().catch(() => {
        video.load();
        const retry = () => play().catch(() => {});
        if (video.readyState >= 2) retry();
        else video.addEventListener('loadeddata', retry, { once: true });
      });
    }

    closeModal() {
      if (!this.modal || this.modal.hasAttribute('hidden')) return;
      this.stage.replaceChildren();
      this.modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onKeydown);
      this.lastTrigger?.focus();
    }
  }

  if (!customElements.get('sh-social-community')) {
    customElements.define('sh-social-community', ShSocialCommunity);
  }
})();
